import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../utils/Hooks";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { LoggedInView } from "../common/LoggedInView";
import Breadcrumbs from "../common/Breadcrumbs";
import ElogSelect, { Option } from "../common/form/ElogSelect";
import { BreadcrumbLink } from "../../models/BreadcrumbLink";
import {
    clearCurrentInquiry,
    getCurrentInquiry,
    resetUpdateInquiryStatus,
    updateInquiry,
} from "../../store/reducers/InquiriesSlice";
import { RequestStatus } from "../../utils/Enums";
import RouteInput from "./components/Routenput";
import ClientInput from "../common/form/ClientInput";
import ElogTextarea from "../common/form/ElogTextarea";
import WeightInput from "../common/form/WeightInput";
import { getCountries } from "../../store/reducers/CountriesSlice";
import { getServices } from "../../store/reducers/ServicesSlice";
import { getUsers } from "../../store/reducers/UsersSlice";
import ElogInput from "../common/form/ElogInput";
import { FormikProvider, useFormik, Form } from "formik";
import {clearRecoveredFormAfterSuccessfulSave} from "../../utils/RecoveredFormStorage";

interface ModifyInquiryScreenProps { }

export interface ModifyInquiryFormProps {
    user: number;
    expires_at: string;
}

const AUTOSAVE_FLAG = '__autosaved';

export const ModifyInquiryScreen: React.FC<ModifyInquiryScreenProps> = () => {
    const { id } = useParams();
    const STORAGE_KEY = id ? "modifyInquiryFormData" + id : "";

    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    const inquiriesState = useAppSelector((state) => state.inquiries);
    const countriesState = useAppSelector((state) => state.countries);
    const servicesState = useAppSelector((state) => state.services);
    const usersState = useAppSelector((state) => state.users);

    const requestStatus: RequestStatus = inquiriesState.updateInquiryStatus;

    const breadcrumbLinks: BreadcrumbLink[] = [
        { text: "Päringud", url: "/inquiries" },
        { text: "Muuda päringut", url: `/inquiries/${id}/modify` },
    ];

    const resetStorage = () => {
        if (STORAGE_KEY) sessionStorage.removeItem(STORAGE_KEY);
    };

    const getSavedValues = () => {
        if (!STORAGE_KEY) return null;
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        try {
            const parsed = JSON.parse(raw);
            if (parsed?.[AUTOSAVE_FLAG] !== true) return null;
            const {[AUTOSAVE_FLAG]: _autosaved, ...values} = parsed;
            return values;
        } catch {
            return null;
        }
    };

    const [hydrated, setHydrated] = useState(false);
    const [loadedInquiryId, setLoadedInquiryId] = useState<string | null>(null);

    useEffect(() => {
        dispatch(resetUpdateInquiryStatus());
        dispatch(getCountries());
        dispatch(getServices(false));
        dispatch(getUsers());
    }, [dispatch, location]);

    useEffect(() => {
        if (id) {
            setHydrated(false);
            setLoadedInquiryId(null);
            let active = true;
            dispatch(getCurrentInquiry({ id: parseInt(id) }))
                .unwrap()
                .then((inquiry) => {
                    if (active && inquiry?.id === parseInt(id)) {
                        setLoadedInquiryId(String(inquiry.id));
                    }
                })
                .catch(() => {});

            return () => {
                active = false;
                dispatch(clearCurrentInquiry());
            };
        }
        dispatch(clearCurrentInquiry());
    }, [id, dispatch]);

    const formik = useFormik({
        initialValues: {
            startCountry: "",
            startCity: "",
            destinationCountry: "",
            destinationCity: "",
            service: "",
            comment: "",
            amount: "",
            weight: "",
            noWeight: false,
            client: "",
            clientContact: "",
            buySum: "",
            saleSum: "",
            user: "",
        },
        onSubmit: (values: any) => {
            const saveRequest = dispatch(updateInquiry({ id, data: values })).unwrap();
            void clearRecoveredFormAfterSuccessfulSave(saveRequest, STORAGE_KEY);
        },
        enableReinitialize: false
    });

    useEffect(() => {
        if (loadedInquiryId !== id || !id || !inquiriesState.currentInquiry || inquiriesState.currentInquiry.id !== parseInt(id)) return;
        if (hydrated || requestStatus === RequestStatus.Success) return;

        const savedValues = getSavedValues();

        const nextValues: any = {
            startCountry: savedValues?.startCountry ?? inquiriesState.currentInquiry.start_country_id ?? "",
            startCity: savedValues?.startCity ?? inquiriesState.currentInquiry.start_city ?? "",
            destinationCountry: savedValues?.destinationCountry ?? inquiriesState.currentInquiry.destination_country_id ?? "",
            destinationCity: savedValues?.destinationCity ?? inquiriesState.currentInquiry.destination_city ?? "",
            service: savedValues?.service ?? inquiriesState.currentInquiry.service_id ?? "",
            comment: savedValues?.comment ?? inquiriesState.currentInquiry.comment ?? "",
            amount: savedValues?.amount ?? inquiriesState.currentInquiry.amount ?? "",
            weight: savedValues?.weight ?? inquiriesState.currentInquiry.weight ?? "",
            noWeight: savedValues?.noWeight ?? (inquiriesState.currentInquiry.no_weight === 1),
            client: savedValues?.client ?? inquiriesState.currentInquiry.client?.name ?? "",
            clientContact: savedValues?.clientContact ?? inquiriesState.currentInquiry.client_contact_id ?? "",
            buySum: savedValues?.buySum ?? inquiriesState.currentInquiry.buy_sum ?? "",
            saleSum: savedValues?.saleSum ?? inquiriesState.currentInquiry.sale_sum ?? "",
            user: savedValues?.user ?? inquiriesState.currentInquiry.user_id ?? "",
        };

        formik.resetForm({ values: nextValues });
        setHydrated(true);
    }, [id, inquiriesState.currentInquiry, hydrated, requestStatus, loadedInquiryId]);

    useEffect(() => {
        if (hydrated && formik.dirty && requestStatus !== RequestStatus.Success) {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify({...formik.values, [AUTOSAVE_FLAG]: true}));
        } else if (hydrated && !formik.dirty && !getSavedValues()) {
            resetStorage();
        }
    }, [formik.values, formik.dirty, hydrated, STORAGE_KEY, requestStatus]);

    useEffect(() => {
        if (requestStatus === RequestStatus.Success) {
            resetStorage();
        }
    }, [requestStatus]);

    const countryOptions: Option[] = countriesState.countries.map((country) => ({
        value: country.id.toString(),
        label: country.name,
    }));

    const serviceOptions: Option[] = servicesState.services.map((service) => ({
        value: service.id.toString(),
        label: service.name,
    }));

    const userOptions: Option[] = usersState.users
        .filter((user) => user.logistician === 1 || user.is_checker === 1)
        .map(user => ({
            value: user.id.toString(),
            label: `${user.firstname} ${user.lastname}`,
        }));

    const handleCancel = () => {
        resetStorage();
        navigate(-1);
    };

    const { handleSubmit } = formik;

    return (
        <LoggedInView fullWidth={false}>
            <Breadcrumbs links={breadcrumbLinks} />
            {requestStatus === RequestStatus.Success && (
                <div className="message msg-ok">
                    <div>
                        <h2>Andmed muudetud</h2>
                    </div>
                </div>
            )}
            {requestStatus === RequestStatus.Failed &&
            inquiriesState.errors.length > 0 && (
                <div className="message msg-error">
                    <div>
                        <h2>Viga:</h2>
                        {inquiriesState.errors.map((error, index) => (
                            <span key={index}>{error}<br /></span>
                        ))}
                    </div>
                </div>
            )}
            <FormikProvider value={formik}>
                <Form onSubmit={handleSubmit}>
                    <div className="form">
                        <h2>
                            <span>Päringu andmed</span>
                        </h2>
                        {inquiriesState.currentInquiry &&
                        <div style={{ marginBottom: 10 }}>
                            <Link to={`/inquiries/${inquiriesState.currentInquiry.id}/recommended`} style={{ marginLeft: 3, marginRight: 6, fontSize: 16 }}>⛟</Link>
                            <a href={inquiriesState.currentInquiry.client?.name ? '/orders?startCountry=0&fromDate=01.01.2015&toDate=&user=-1&destinationCountry=0&status=all&perpage=80&client=' + inquiriesState.currentInquiry.client.name + '&service=0&amountFrom=&amountTo=&minus=0&nooffer=0&tab=' : ''} style={{ marginLeft: 3, marginRight: 6, fontSize: 14 }}>$</a>
                            <a href={inquiriesState.currentInquiry.client?.name ? '/inquiries?fromDate=01.01.2015&toDate=31.12.' + new Date().getFullYear() + '&client=' + inquiriesState.currentInquiry.client.name + '&status=all' : ''} style={{ marginLeft: 3, marginRight: 6, fontSize: 14 }}>✉️</a>
                        </div>
                        }
                        <table className="form">
                            <tbody>
                            <ElogSelect label="Logistik" name="user" options={userOptions} mandatory={true} />
                            <RouteInput countries={countryOptions} />
                            <ClientInput storageKey={STORAGE_KEY} />
                            <ElogSelect label="Teenuse liik" name="service" options={serviceOptions} mandatory={true} />
                            <ElogTextarea label="Kommentaar" name="comment" mandatory={false} />
                            <ElogTextarea label="Kaubakogus" name="amount" mandatory={true} />
                            <WeightInput />
                            {inquiriesState?.currentInquiry?.state === 'answered' &&
                            <>
                                <ElogInput label="Ostu summa" name="buySum" />
                                <ElogInput label="Müügi summa" name="saleSum" />
                            </>
                            }
                            </tbody>
                        </table>
                    </div>
                    <div className="action clear">
                        <p>
                            <button type="submit">
                                <span>
                                    <strong>Kinnita</strong>
                                </span>
                            </button>
                            <Link to="" onClick={handleCancel}>Tühista</Link>
                        </p>
                    </div>
                </Form>
            </FormikProvider>
        </LoggedInView>
    );
};
